import { useState } from "react";

/**
 * VisionLogo
 * ----------
 * Renders the real Vision brand SVG from /public/vision-logo.svg.
 * The uploaded SVG is a horizontal lock-up (sunburst mark + "VISION
 * FACILITIES SECURITY" wordmark baked into the file), so we render it
 * at its native aspect ratio and do NOT add any extra wordmark.
 *
 * `size` = the target HEIGHT (px). Width is derived from the SVG's own
 * 481.89 × 198.43 viewBox to preserve aspect ratio (never crop, never
 * recolor).
 *
 * On dark backgrounds (tone="white", i.e. the Login screen) we place the
 * mark on a soft white "clear space" plate — the artwork is untouched;
 * we're only giving it the clear space around the mark that the brand
 * rules require, so the navy wordmark remains legible on the navy
 * gradient.
 */

const LOGO_ASPECT = 481.89 / 198.43; // ≈ 2.428

export const VisionLogo = ({
  size = 44,
  tone = "color",       // "color" (default) | "white" (dark backgrounds)
  onPlate = "auto",     // "auto" | true | false — plate for dark bgs
  className = "",
}) => {
  const [imgFailed, setImgFailed] = useState(false);
  const [srcIdx, setSrcIdx] = useState(0);
  const candidates = ["/vision-logo.svg", "/vision-logo.png"];
  const currentSrc = candidates[srcIdx];

  const useUploaded = !imgFailed && srcIdx < candidates.length;
  const width = Math.round(size * LOGO_ASPECT);

  // Auto-plate: white rounded backdrop only when tone is "white" (dark bg).
  const showPlate = onPlate === true || (onPlate === "auto" && tone === "white");

  const img = useUploaded ? (
    <img
      src={currentSrc}
      alt="Vision — Facilities and Security"
      width={width}
      height={size}
      data-testid="vision-logo-uploaded"
      style={{
        width,
        height: size,
        objectFit: "contain",
        display: "block",
      }}
      onError={() => {
        if (srcIdx + 1 < candidates.length) setSrcIdx(srcIdx + 1);
        else setImgFailed(true);
      }}
    />
  ) : (
    // Fallback — only shown if the SVG/PNG file is missing.
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      role="img"
      aria-label="Vision"
      data-testid="vision-logo-placeholder"
    >
      <g stroke={tone === "white" ? "#FFFFFF" : "#F26222"} strokeWidth="3.2" strokeLinecap="round">
        {[...Array(12)].map((_, i) => {
          const a = (i * 30 * Math.PI) / 180;
          const x1 = 32 + Math.cos(a) * 18;
          const y1 = 32 + Math.sin(a) * 18;
          const x2 = 32 + Math.cos(a) * 28;
          const y2 = 32 + Math.sin(a) * 28;
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />;
        })}
      </g>
      <circle cx="32" cy="32" r="11" fill={tone === "white" ? "#FFFFFF" : "#2E3192"} />
      <circle cx="32" cy="32" r="4.5" fill={tone === "white" ? "#FFFFFF" : "#F26222"} />
    </svg>
  );

  if (showPlate) {
    // Padded white plate, no changes to the artwork itself.
    return (
      <div
        className={`inline-flex items-center rounded-2xl bg-white shadow-card ${className}`}
        style={{ padding: `${Math.round(size * 0.22)}px ${Math.round(size * 0.32)}px` }}
        data-testid="vision-logo"
      >
        {img}
      </div>
    );
  }

  return (
    <div className={`inline-flex items-center ${className}`} data-testid="vision-logo">
      {img}
    </div>
  );
};

export default VisionLogo;
